/**
 * Role Management Component
 * 
 * Allows developers to customize permissions for each role.
 * Shows 4 containers: Developer, Admin, User, and Custom roles.
 * Each container lists all permissions with checkboxes that auto-save.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { PERMISSIONS } from '../config/permissions';

// Permission categories for better organization
const PERMISSION_CATEGORIES = {
  'Page Access': [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_REPAIRS,
    PERMISSIONS.VIEW_MAP,
    PERMISSIONS.VIEW_ASSETS,
    PERMISSIONS.VIEW_ADMIN,
    PERMISSIONS.VIEW_SETTINGS,
  ],
  'Organization Management': [
    PERMISSIONS.MANAGE_ALL_ORGANIZATIONS,
    PERMISSIONS.MANAGE_OWN_ORGANIZATIONS,
    PERMISSIONS.ADD_ORGANIZATION,
  ],
  'User Management': [
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.MANAGE_USERS,
  ],
  'File Management': [
    PERMISSIONS.VIEW_FILES,
    PERMISSIONS.MANAGE_FILES,
  ],
  'Repair Management': [
    PERMISSIONS.VIEW_ALL_REPAIRS,
    PERMISSIONS.VIEW_OWN_REPAIRS,
    PERMISSIONS.CREATE_REPAIR,
    PERMISSIONS.MANAGE_REPAIRS,
  ],
  'Admin Features': [
    PERMISSIONS.ADMIN_REPORTING,
    PERMISSIONS.SYSTEM_SETTINGS,
  ],
};

// Human-readable permission labels
const PERMISSION_LABELS = {
  [PERMISSIONS.VIEW_DASHBOARD]: 'View Dashboard',
  [PERMISSIONS.VIEW_REPAIRS]: 'View Repairs',
  [PERMISSIONS.VIEW_MAP]: 'View Map',
  [PERMISSIONS.VIEW_ASSETS]: 'View Assets',
  [PERMISSIONS.VIEW_ADMIN]: 'View Admin Panel',
  [PERMISSIONS.VIEW_SETTINGS]: 'View Settings',
  [PERMISSIONS.MANAGE_ALL_ORGANIZATIONS]: 'Manage All Organizations',
  [PERMISSIONS.MANAGE_OWN_ORGANIZATIONS]: 'Manage Own Organizations',
  [PERMISSIONS.ADD_ORGANIZATION]: 'Add Organization',
  [PERMISSIONS.VIEW_USERS]: 'View Users',
  [PERMISSIONS.MANAGE_USERS]: 'Manage Users',
  [PERMISSIONS.VIEW_FILES]: 'View Files',
  [PERMISSIONS.MANAGE_FILES]: 'Manage Files',
  [PERMISSIONS.VIEW_ALL_REPAIRS]: 'View All Repairs',
  [PERMISSIONS.VIEW_OWN_REPAIRS]: 'View Own Repairs',
  [PERMISSIONS.CREATE_REPAIR]: 'Create Repair',
  [PERMISSIONS.MANAGE_REPAIRS]: 'Manage Repairs',
  [PERMISSIONS.ADMIN_REPORTING]: 'Admin Reporting',
  [PERMISSIONS.SYSTEM_SETTINGS]: 'System Settings',
};

const ROLES = ['developer', 'admin', 'user', 'custom'];

export default function RoleManagement() {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState(null);

  // Load permissions on mount
  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/role-permissions');
      setPermissions(response || {});
    } catch (err) {
      console.error('Failed to load role permissions:', err);
      setError('Failed to load role permissions. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const updatePermission = useCallback(async (roleName, permissionKey, hasAccess) => {
    // Optimistically update UI
    setPermissions(prev => ({
      ...prev,
      [roleName]: {
        ...prev[roleName],
        [permissionKey]: hasAccess
      }
    }));

    // Set saving state
    setSaving(prev => ({ ...prev, [`${roleName}-${permissionKey}`]: true }));

    try {
      await api.put(`/api/role-permissions/${roleName}/${permissionKey}`, {
        has_access: hasAccess
      });
    } catch (err) {
      console.error('Failed to update permission:', err);
      // Revert on error
      setPermissions(prev => ({
        ...prev,
        [roleName]: {
          ...prev[roleName],
          [permissionKey]: !hasAccess
        }
      }));
      alert(`Failed to update permission: ${err.response?.data?.message || err.message}`);
    } finally {
      setSaving(prev => {
        const newState = { ...prev };
        delete newState[`${roleName}-${permissionKey}`];
        return newState;
      });
    }
  }, []);

  const handleCheckboxChange = (roleName, permissionKey) => {
    const currentValue = permissions[roleName]?.[permissionKey] || false;
    updatePermission(roleName, permissionKey, !currentValue);
  };

  // Get all permission keys
  const allPermissions = Object.values(PERMISSIONS);

  if (loading) {
    return (
      <div className={`p-8 text-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
        Loading role permissions...
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-8 text-center ${isDark ? 'text-red-400' : 'text-red-600'}`}>
        {error}
        <button
          onClick={loadPermissions}
          className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Role Permissions Management
        </h2>
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Customize what each role can access. Changes are saved automatically.
        </p>
      </div>

      <div className="flex flex-row gap-4 overflow-x-auto pb-4 -mx-6 px-6">
        {ROLES.map(roleName => (
          <RoleContainer
            key={roleName}
            roleName={roleName}
            permissions={permissions[roleName] || {}}
            onPermissionChange={handleCheckboxChange}
            saving={saving}
            isDark={isDark}
          />
        ))}
      </div>
    </div>
  );
}

function RoleContainer({ roleName, permissions, onPermissionChange, saving, isDark }) {
  const roleDisplayName = roleName.charAt(0).toUpperCase() + roleName.slice(1);
  const roleColors = {
    developer: { bg: 'bg-green-900/30', border: 'border-green-500', text: 'text-green-400' },
    admin: { bg: 'bg-red-900/30', border: 'border-red-500', text: 'text-red-400' },
    user: { bg: 'bg-blue-900/30', border: 'border-blue-500', text: 'text-blue-400' },
    custom: { bg: 'bg-orange-900/30', border: 'border-orange-500', text: 'text-orange-400' },
  };

  const colors = roleColors[roleName] || roleColors.user;

  return (
    <div className={`flex-shrink-0 w-80 rounded-lg border-2 ${colors.border} ${colors.bg} ${isDark ? 'bg-gray-800/50' : 'bg-white'} overflow-hidden flex flex-col h-[800px]`}>
      {/* Header */}
      <div className={`px-4 py-3 border-b ${colors.border} ${isDark ? 'bg-gray-800/70' : 'bg-gray-50'} flex-shrink-0`}>
        <h3 className={`text-lg font-bold ${colors.text}`}>
          {roleDisplayName} Role
        </h3>
        <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          {Object.values(permissions).filter(Boolean).length} of {Object.values(PERMISSIONS).length} permissions enabled
        </p>
      </div>

      {/* Permissions List */}
      <div className="p-4 overflow-y-auto flex-1">
        {Object.entries(PERMISSION_CATEGORIES).map(([category, categoryPermissions]) => (
          <div key={category} className="mb-4 last:mb-0">
            <h4 className={`text-xs font-semibold mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              {category}
            </h4>
            <div className="space-y-1.5">
              {categoryPermissions.map(permissionKey => {
                const hasAccess = permissions[permissionKey] || false;
                const isSaving = saving[`${roleName}-${permissionKey}`];
                const label = PERMISSION_LABELS[permissionKey] || permissionKey;

                // Get checkbox color classes based on role
                const getCheckboxClasses = () => {
                  if (!hasAccess) {
                    return isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-white';
                  }
                  // Role-specific colors when checked
                  if (roleName === 'developer') return 'border-green-500 bg-green-600';
                  if (roleName === 'admin') return 'border-red-500 bg-red-600';
                  if (roleName === 'user') return 'border-blue-500 bg-blue-600';
                  if (roleName === 'custom') return 'border-orange-500 bg-orange-600';
                  return 'border-gray-500 bg-gray-600';
                };

                // Get background color for checked items
                const getItemBgClasses = () => {
                  if (!hasAccess) {
                    return isDark ? 'bg-gray-700/30 hover:bg-gray-700/50' : 'bg-gray-50 hover:bg-gray-100';
                  }
                  if (roleName === 'developer') return isDark ? 'bg-green-900/20 hover:bg-green-900/30' : 'bg-green-50 hover:bg-green-100';
                  if (roleName === 'admin') return isDark ? 'bg-red-900/20 hover:bg-red-900/30' : 'bg-red-50 hover:bg-red-100';
                  if (roleName === 'user') return isDark ? 'bg-blue-900/20 hover:bg-blue-900/30' : 'bg-blue-50 hover:bg-blue-100';
                  if (roleName === 'custom') return isDark ? 'bg-orange-900/20 hover:bg-orange-900/30' : 'bg-orange-50 hover:bg-orange-100';
                  return isDark ? 'bg-gray-700/30 hover:bg-gray-700/50' : 'bg-gray-50 hover:bg-gray-100';
                };

                return (
                  <label
                    key={permissionKey}
                    className={`flex items-center justify-between p-2 rounded-md cursor-pointer transition-colors ${getItemBgClasses()} ${isSaving ? 'opacity-50' : ''}`}
                  >
                    <span className={`flex-1 text-xs ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      {label}
                    </span>
                    <div className="flex items-center ml-2">
                      {isSaving && (
                        <span className={`text-xs mr-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Saving...
                        </span>
                      )}
                      <input
                        type="checkbox"
                        checked={hasAccess}
                        onChange={() => onPermissionChange(roleName, permissionKey)}
                        disabled={isSaving}
                        className={`w-4 h-4 rounded border-2 ${getCheckboxClasses()} text-white focus:ring-2 focus:ring-offset-0 ${colors.border.replace('border-', 'focus:ring-')} cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`}
                      />
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
